import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { FileText, Shield, Scale, Database, ArrowLeft } from 'lucide-react';
import { termsContent, privacyContent, acceptableUseContent, dpaContent } from './legalContent';

const policies = [
  { path: '/legal/terms', label: 'Terms of Service', icon: FileText, desc: 'Rules governing your use of Practicio.' },
  { path: '/legal/privacy', label: 'Privacy Policy', icon: Shield, desc: 'How we collect, use, and protect your data.' },
  { path: '/legal/acceptable-use', label: 'Acceptable Use Policy', icon: Scale, desc: 'Guidelines for appropriate platform usage.' },
  { path: '/legal/dpa', label: 'Data Processing Agreement', icon: Database, desc: 'How we process data on your behalf.' },
];

function LegalPage({ markdown }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/legal" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-8">
          <ArrowLeft className="w-4 h-4" /> All Policies
        </Link>
        <article className="bg-white border border-slate-200 rounded-lg p-8 prose prose-slate prose-sm max-w-none prose-h1:text-2xl prose-h1:mb-4 prose-h2:text-lg prose-h2:mt-8 prose-h2:mb-3 prose-h3:text-base prose-p:leading-relaxed prose-li:leading-relaxed prose-hr:my-6 prose-table:text-sm prose-strong:text-slate-800" data-testid="legal-document-content">
          <ReactMarkdown>{markdown}</ReactMarkdown>
        </article>
        <LegalFooterLinks className="mt-12" />
      </div>
    </div>
  );
}

export function LegalFooterLinks({ className = '' }) {
  return (
    <div className={`flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400 ${className}`}>
      {policies.map(p => (
        <Link key={p.path} to={p.path} target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 transition-colors" data-testid={`legal-link-${p.path.split('/').pop()}`}>
          {p.label}
        </Link>
      ))}
    </div>
  );
}

export default function LegalIndex() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-xl font-bold text-slate-900 mb-2" data-testid="legal-page-title">Legal & Policies</h1>
        <p className="text-slate-500 mb-10">Review Practicio's policies and legal documents.</p>
        <div className="space-y-3">
          {policies.map(p => (
            <Link
              key={p.path}
              to={p.path}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-lg hover:border-slate-400 transition-colors group"
              data-testid={`legal-card-${p.path.split('/').pop()}`}
            >
              <div className="bg-slate-100 rounded-lg p-2.5 group-hover:bg-slate-200 transition-colors">
                <p.icon className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm">{p.label}</p>
                <p className="text-xs text-slate-400 mt-0.5">{p.desc}</p>
              </div>
            </Link>
          ))}
        </div>
        <LegalFooterLinks className="mt-12" />
      </div>
    </div>
  );
}

export function TermsPage() {
  return <LegalPage markdown={termsContent} />;
}

export function PrivacyPage() {
  return <LegalPage markdown={privacyContent} />;
}

export function AcceptableUsePage() {
  return <LegalPage markdown={acceptableUseContent} />;
}

export function DpaPage() {
  return <LegalPage markdown={dpaContent} />;
}
